/**
 * FILE: RewardsModal.js (V2.0 — Live Edition)
 * PURPOSE: "Points & Achievements" wallet connected live to the Coins Engine.
 *
 * Changes from V1:
 *   - POINTS tab: balance + history fetched live from /api/coins/balance & /api/coins/history
 *   - SPEND tab: POLL_BOOST, IDENTITY_SHIELD buttons call /api/coins/spend (DATA_UNLOCK
 *     is deliberately not offered here — it's poll-scoped; see the "Unlock Results"
 *     button on each poll card instead, which calls /api/user/unlock directly)
 *   - STREAK tab: unchanged, still driven by streak prop
 *   - ipBalance prop still accepted for immediate display; ledger balance loads async
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    X, Wallet, Flame, TrendingUp, ArrowRight,
    History, ShieldCheck, Zap, Star, Gift,
    CheckCircle2, AlertCircle, Rocket, Lock,
    RefreshCw, ShoppingBag, ChevronRight, Loader2, Undo2
} from 'lucide-react';
import { useModalA11y } from '../hooks/useModalA11y';
import { api } from '../utils/api';

// ─── Animated count-up hook ───────────────────────────────────────────────────
/**
 * Counts from 0 → target whenever `target` changes.
 * Uses requestAnimationFrame + an easeOut curve for a satisfying meter effect.
 */
function useCountUp(target, duration = 1400) {
    const [display, setDisplay] = useState(0);
    const rafRef = useRef(null);

    useEffect(() => {
        if (target === 0) { setDisplay(0); return; }
        const start = performance.now();
        const from = 0; // always sweep from 0 for the "meter" feel

        const tick = (now) => {
            const elapsed = now - start;
            const progress = Math.min(elapsed / duration, 1);
            // easeOut cubic
            const eased = 1 - Math.pow(1 - progress, 3);
            setDisplay(Math.round(from + (target - from) * eased));
            if (progress < 1) rafRef.current = requestAnimationFrame(tick);
        };

        rafRef.current = requestAnimationFrame(tick);
        return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
    }, [target, duration]);

    return display;
}

// ─── Type badge colours ───────────────────────────────────────────────────────
const TYPE_COLORS = {
    VOTE_REWARD: { bg: 'bg-green-100 dark:bg-green-900/30', text: 'text-green-600 dark:text-green-400', emoji: '🗳️' },
    STREAK_BONUS: { bg: 'bg-orange-100 dark:bg-orange-900/30', text: 'text-orange-600 dark:text-orange-400', emoji: '🔥' },
    ROYALTY: { bg: 'bg-amber-100 dark:bg-amber-900/30', text: 'text-amber-600 dark:text-amber-400', emoji: '👑' },
    PROFILING_BONUS: { bg: 'bg-blue-100 dark:bg-blue-900/30', text: 'text-blue-600 dark:text-blue-400', emoji: '🔬' },
    POLL_BOOST: { bg: 'bg-purple-100 dark:bg-purple-900/30', text: 'text-purple-600 dark:text-purple-400', emoji: '🚀' },
    DATA_UNLOCK: { bg: 'bg-teal-100 dark:bg-teal-900/30', text: 'text-teal-600 dark:text-teal-400', emoji: '🔓' },
    IDENTITY_SHIELD: { bg: 'bg-indigo-100 dark:bg-indigo-900/30', text: 'text-indigo-600 dark:text-indigo-400', emoji: '🛡️' },
    ROLLBACK: { bg: 'bg-red-100 dark:bg-red-900/30', text: 'text-red-600 dark:text-red-400', icon: <Undo2 size={16} className="text-red-500" /> },
};

function relativeTime(isoStr) {
    if (!isoStr) return '';
    const diff = Date.now() - new Date(isoStr).getTime();
    const m = Math.floor(diff / 60000);
    if (m < 1) return 'Just now';
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    return `${Math.floor(h / 24)}d ago`;
}

// ─── Main Modal ───────────────────────────────────────────────────────────────

export function RewardsModal({ isOpen, onClose, ipBalance, streak, darkMode, anonymousId, onBalanceUpdate, votedPollsList = [], onGoVote }) {
    const [activeTab, setActiveTab] = useState('points');

    // Live data state — always starts at 0, populated by the ledger API on open
    const [balance, setBalance] = useState(0);
    const [history, setHistory] = useState([]);
    const [histLoading, setHistLoading] = useState(false);
    const [histError, setHistError] = useState(null);

    // Load live data whenever modal opens
    const loadData = useCallback(async () => {
        if (!anonymousId) return;
        setHistLoading(true);
        setHistError(null);
        try {
            const [balRes, histRes] = await Promise.all([
                api.getLedgerBalance(anonymousId).catch(() => null),
                api.getCoinHistory(anonymousId).catch(() => null),
            ]);
            if (balRes?.balance !== undefined) {
                setBalance(balRes.balance);
                // Push verified balance back to the header so they stay in sync
                if (onBalanceUpdate) onBalanceUpdate(balRes.balance);
            }
            if (histRes?.transactions) setHistory(histRes.transactions);
        } catch {
            setHistError('Could not load live data.');
        } finally {
            setHistLoading(false);
        }
    }, [anonymousId, onBalanceUpdate]);

    useEffect(() => {
        if (isOpen) loadData();
    }, [isOpen, loadData]);

    const containerRef = useModalA11y(isOpen, onClose);

    if (!isOpen) return null;

    const TABS = [
        { id: 'points', label: 'My Coins', icon: <Wallet size={15} />, activeColor: 'text-primary-600' },
        { id: 'spend', label: 'Spend', icon: <ShoppingBag size={15} />, activeColor: 'text-emerald-600' },
        { id: 'streak', label: 'Daily Streak', icon: <Flame size={15} />, activeColor: 'text-secondary-600' },
    ];

    return (
        <div className="fixed inset-0 z-[150] flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/60 backdrop-blur-md animate-in fade-in duration-300" onClick={onClose} />

            <div ref={containerRef} role="dialog" aria-modal="true" aria-label="Your Rewards" className={`relative w-full max-w-2xl max-h-[90vh] rounded-modal shadow-2xl animate-in zoom-in-95 duration-300 border flex flex-col overflow-hidden ${darkMode ? 'bg-gray-900 border-gray-800 text-white' : 'bg-white border-gray-100 text-[#1F2937]'}`}>

                {/* Header */}
                <div className={`px-8 pt-8 pb-4 flex flex-col space-y-6 border-b ${darkMode ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-100'}`}>
                    <div className="flex justify-between items-center">
                        <h2 className="text-3xl font-black italic uppercase tracking-tighter">Your Rewards</h2>
                        <div className="flex items-center gap-3">
                            {anonymousId && (
                                <button onClick={loadData} disabled={histLoading} aria-label="Refresh balance" className={`p-2 rounded-full transition-all ${darkMode ? 'bg-gray-800 text-gray-500 hover:text-white' : 'bg-gray-100 text-gray-500 hover:text-gray-900'}`}>
                                    <RefreshCw size={16} className={histLoading ? 'animate-spin' : ''} aria-hidden="true" />
                                </button>
                            )}
                            <button onClick={onClose} aria-label="Close rewards" className={`p-2.5 rounded-full transition-all ${darkMode ? 'bg-gray-800 text-gray-500 hover:text-white' : 'bg-gray-100 text-gray-500 hover:text-gray-900'}`}>
                                <X size={24} aria-hidden="true" />
                            </button>
                        </div>
                    </div>

                    {/* Tab Switcher */}
                    <div className="flex space-x-1 justify-center p-1 bg-gray-100 dark:bg-gray-800 rounded-2xl">
                        {TABS.map(tab => (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id)}
                                className={`flex-1 py-3 px-3 rounded-xl text-2xs font-black uppercase tracking-widest transition-all flex items-center justify-center space-x-1.5 ${activeTab === tab.id
                                    ? `bg-white dark:bg-gray-700 ${tab.activeColor} shadow-sm`
                                    : 'text-gray-500 hover:text-gray-600 dark:hover:text-gray-200'}`}
                            >
                                {tab.icon}
                                <span>{tab.label}</span>
                            </button>
                        ))}
                    </div>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-8 no-scrollbar">
                    {activeTab === 'points' && (
                        <PointsTab
                            balance={balance}
                            history={history}
                            histLoading={histLoading}
                            histError={histError}
                            loggedIn={!!anonymousId}
                            darkMode={darkMode}
                        />
                    )}
                    {activeTab === 'spend' && (
                        <SpendTab
                            balance={balance}
                            anonymousId={anonymousId}
                            darkMode={darkMode}
                            onSpendSuccess={(newBalance) => setBalance(newBalance)}
                        />
                    )}
                    {activeTab === 'streak' && (
                        <StreakTab streak={streak} darkMode={darkMode} votedPollsList={votedPollsList} onGoVote={onGoVote} />
                    )}
                </div>

                <div className={`px-8 py-4 border-t ${darkMode ? 'border-gray-800 bg-gray-900/50' : 'border-gray-100 bg-gray-50/50'}`}>
                    <p className="text-center text-2xs font-bold text-gray-500 italic">Voting daily secures your progress.</p>
                </div>
            </div>
        </div>
    );
}

// ─── Points Tab ───────────────────────────────────────────────────────────────

function PointsTab({ balance, history, histLoading, histError, loggedIn, darkMode }) {
    const animatedBalance = useCountUp(balance, 1400);

    return (
        <div className="space-y-10 animate-in fade-in slide-in-from-bottom-4 duration-500">

            {/* Balance hero */}
            <div className="text-center">
                <p className="text-sm font-black text-gray-500 uppercase tracking-widest mb-2">My Coin Balance</p>
                <div className="flex items-center justify-center space-x-3 mb-3">
                    <div className="w-12 h-12 rounded-2xl bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                        <Star className="text-purple-600 dark:text-purple-400" size={28} />
                    </div>
                    <span className="text-8xl font-black text-gray-900 dark:text-white tabular-nums tracking-tighter" aria-hidden="true">
                        {animatedBalance.toLocaleString()}
                    </span>
                    {/* Hidden live region: announces the settled final value to screen readers */}
                    <span className="sr-only" aria-live="polite">{balance.toLocaleString()} coins</span>
                    <span className="text-xl font-black text-purple-500 uppercase self-end mb-4">Coins</span>
                </div>
                <div className="inline-flex items-center px-4 py-1.5 rounded-full bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400 text-xs font-black uppercase tracking-widest italic border border-green-100 dark:border-green-900/30">
                    <TrendingUp size={14} className="mr-2" />
                    {loggedIn ? 'Ledger Verified' : 'Sign in to track earnings'}
                </div>
            </div>

            {/* How to earn */}
            <section>
                <div className="flex items-center space-x-3 mb-6">
                    <h4 className="text-lg font-black tracking-tight text-gray-900 dark:text-white">Ways to Earn</h4>
                    <div className="h-[2px] flex-1 bg-gray-100 dark:bg-gray-800 rounded-full" />
                </div>
                <div className="grid grid-cols-1 gap-4">
                    {[
                        { emoji: '🗳️', title: 'Vote on Polls', sub: 'Verified vote via Odometer', reward: '+5 Pts', color: 'text-green-500' },
                        { emoji: '🔥', title: 'Keep a Streak', sub: 'Bonus kicks in at day 5+', reward: 'Up to ×3', color: 'text-orange-500' },
                        { emoji: '👑', title: 'Creator Royalty', sub: '10% when your poll data sells', reward: '10%', color: 'text-amber-500' },
                        { emoji: '🔬', title: 'Security Check', sub: 'Complete a profiling quiz', reward: '+50 Pts', color: 'text-blue-500' },
                    ].map(item => (
                        <div key={item.title} className={`p-5 rounded-3xl border flex items-center justify-between transition-all ${darkMode ? 'bg-gray-800/40 border-gray-700 hover:border-purple-500/50' : 'bg-white border-gray-200 shadow-sm hover:border-purple-500/50'}`}>
                            <div className="flex items-center space-x-5">
                                <div className="w-12 h-12 rounded-2xl bg-gray-100 dark:bg-gray-800 flex items-center justify-center text-2xl">{item.emoji}</div>
                                <div>
                                    <h5 className="font-black text-sm tracking-tight text-gray-900 dark:text-white">{item.title}</h5>
                                    <p className="text-xs font-bold text-gray-500 italic">{item.sub}</p>
                                </div>
                            </div>
                            <span className={`text-base font-black ${item.color}`}>{item.reward}</span>
                        </div>
                    ))}
                </div>
            </section>

            {/* Live transaction history */}
            <section>
                <div className="flex items-center space-x-3 mb-5 text-gray-500">
                    <History size={16} />
                    <h4 className="text-xs font-black uppercase tracking-widest text-gray-500">Transaction History</h4>
                    <div className="h-[2px] flex-1 bg-gray-100 dark:bg-gray-800 rounded-full opacity-20" />
                </div>

                {!loggedIn ? (
                    <div className={`p-6 rounded-2xl border border-dashed text-center ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                        <Lock size={24} className="mx-auto mb-2 text-gray-500" />
                        <p className="text-xs font-bold text-gray-500 italic">Sign in to see your history.</p>
                    </div>
                ) : histLoading ? (
                    <div className="flex items-center justify-center py-10 space-x-2 text-gray-500">
                        <Loader2 size={18} className="animate-spin" />
                        <span className="text-xs font-bold uppercase tracking-widest">Loading ledger...</span>
                    </div>
                ) : histError ? (
                    <div className="p-4 rounded-2xl bg-red-50 dark:bg-red-900/20 text-red-500 text-xs font-bold text-center">{histError}</div>
                ) : history.length === 0 ? (
                    <div className={`p-6 rounded-2xl border border-dashed text-center ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                        <Gift size={24} className="mx-auto mb-2 text-gray-500" />
                        <p className="text-xs font-bold text-gray-500 italic">No transactions yet. Cast your first vote!</p>
                    </div>
                ) : (
                    <div className="space-y-1">
                        {history.slice(0, 15).map((tx, i) => {
                            const style = TYPE_COLORS[tx.type] ?? TYPE_COLORS['VOTE_REWARD'];
                            const isCredit = tx.amount > 0;
                            return (
                                <div key={i} className={`flex items-center justify-between py-3 px-3 rounded-xl border-b last:border-0 transition-colors ${darkMode ? 'border-gray-800 hover:bg-gray-800/40' : 'border-gray-100 hover:bg-gray-50'}`}>
                                    <div className="flex items-center space-x-3">
                                        <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${style.bg}`}>
                                            {style.icon
                                                ? style.icon
                                                : <span className="text-lg" aria-hidden="true">{style.emoji}</span>
                                            }
                                        </div>
                                        <div>
                                            <p className="text-xs font-black text-gray-800 dark:text-gray-100 tracking-tight">{tx.reason || tx.type}</p>
                                            <p className={`text-2xs font-black uppercase tracking-widest ${style.text}`}>{tx.type?.replace(/_/g, ' ')}</p>
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <div className={`text-sm font-black ${isCredit ? 'text-green-500' : 'text-red-500'}`}>
                                            {isCredit ? '+' : ''}{tx.amount}
                                        </div>
                                        <div className="text-2xs text-gray-500 font-bold">{relativeTime(tx.created)}</div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </section>
        </div>
    );
}

// ─── Spend Tab ────────────────────────────────────────────────────────────────

// NOTE: DATA_UNLOCK intentionally is NOT listed here. Unlocking deep poll
// data is inherently scoped to a specific poll (it requires a pollId), and
// this generic Spend Tab has no poll in context to attach it to — calling
// POST /api/coins/spend with type: 'DATA_UNLOCK' and no referenceId always
// fails the endpoint's "referenceId is required for this spend type" check.
// The real, working, per-poll unlock flow lives on each poll card's "Unlock
// Results" button (PollCard.js -> api.unlockPoll -> POST /api/user/unlock).
const SPEND_ACTIONS = [
    {
        type: 'POLL_BOOST',
        emoji: '🚀',
        title: 'Poll Boost',
        desc: 'Push your poll into the public feed faster. Gets more eyes on your question.',
        cost: 50,
        color: 'from-purple-500 to-indigo-500',
        needsRef: false,
    },
    {
        type: 'IDENTITY_SHIELD',
        emoji: '🛡️',
        title: 'Identity Shield',
        desc: 'Hard-reset a failed Confidence Score and start fresh.',
        cost: 100,
        color: 'from-indigo-500 to-blue-500',
        needsRef: false,
    },
];

function SpendTab({ balance, anonymousId, darkMode, onSpendSuccess }) {
    const [spending, setSpending] = useState(null); // which type is loading
    const [result, setResult] = useState(null); // { ok, message }

    const handleSpend = async (action) => {
        if (!anonymousId) {
            setResult({ ok: false, message: 'You must be signed in to spend coins.' });
            return;
        }
        if (balance < action.cost) {
            setResult({ ok: false, message: `You need ${action.cost} coins but only have ${balance}.` });
            return;
        }

        setSpending(action.type);
        setResult(null);
        try {
            const res = await api.spendCoins(anonymousId, action.type, null);
            onSpendSuccess(res.newBalance);
            setResult({ ok: true, message: `${action.title} activated! New balance: ${res.newBalance} coins.` });
        } catch (err) {
            setResult({ ok: false, message: err.message || 'Spend failed. Try again.' });
        } finally {
            setSpending(null);
        }
    };

    return (
        <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">

            {/* Balance pill */}
            <div className="flex items-center justify-between p-5 rounded-2xl bg-violet-50 dark:bg-violet-900/20 border border-violet-100 dark:border-violet-800">
                <div>
                    <p className="text-2xs font-black text-violet-500 uppercase tracking-widest mb-0.5">Available to Spend</p>
                    <p className="text-4xl font-black tabular-nums text-violet-700 dark:text-violet-300">{balance.toLocaleString()}</p>
                </div>
                <div className="text-4xl">💰</div>
            </div>

            {/* Result toast */}
            {result && (
                <div className={`flex items-center space-x-3 p-4 rounded-2xl text-sm font-bold ${result.ok ? 'bg-green-50 dark:bg-green-900/20 text-green-600 border border-green-200 dark:border-green-800' : 'bg-red-50 dark:bg-red-900/20 text-red-600 border border-red-200 dark:border-red-800'}`}>
                    {result.ok ? <CheckCircle2 size={18} className="flex-shrink-0" /> : <AlertCircle size={18} className="flex-shrink-0" />}
                    <span>{result.message}</span>
                </div>
            )}

            {/* Action cards */}
            <div className="space-y-4">
                <h4 className="text-lg font-black tracking-tight text-gray-900 dark:text-white">Available Actions</h4>
                {SPEND_ACTIONS.map((action) => {
                    const canAfford = balance >= action.cost;
                    const isLoading = spending === action.type;
                    return (
                        <div key={action.type} className={`p-6 rounded-3xl border transition-all ${darkMode ? 'bg-gray-800/40 border-gray-700' : 'bg-white border-gray-200 shadow-sm'} ${canAfford ? 'hover:border-violet-400' : 'opacity-60'}`}>
                            <div className="flex items-start justify-between gap-4">
                                <div className="flex items-start space-x-4 flex-1">
                                    <div className={`w-12 h-12 rounded-2xl bg-gradient-to-br ${action.color} flex items-center justify-center text-2xl flex-shrink-0 shadow-lg`}>
                                        {action.emoji}
                                    </div>
                                    <div className="flex-1">
                                        <div className="flex items-center gap-2 mb-1">
                                            <h5 className="font-black text-base tracking-tight text-gray-900 dark:text-white">{action.title}</h5>
                                            <span className="px-2 py-0.5 rounded-full bg-violet-100 dark:bg-violet-900/40 text-violet-600 dark:text-violet-300 text-2xs font-black uppercase tracking-widest">
                                                {action.cost} coins
                                            </span>
                                        </div>
                                        <p className="text-xs font-medium text-gray-500 leading-relaxed">{action.desc}</p>
                                    </div>
                                </div>
                                <button
                                    onClick={() => handleSpend(action)}
                                    disabled={!canAfford || !!spending}
                                    className={`flex-shrink-0 flex items-center space-x-1.5 px-5 py-3 rounded-2xl text-xs font-black uppercase tracking-widest transition-all ${canAfford && !spending
                                        ? `bg-gradient-to-br ${action.color} text-white hover:scale-105 active:scale-95 shadow-lg`
                                        : 'bg-gray-100 dark:bg-gray-800 text-gray-500 cursor-not-allowed'}`}
                                >
                                    {isLoading ? (
                                        <Loader2 size={14} className="animate-spin" />
                                    ) : canAfford ? (
                                        <><Zap size={14} /><span>Spend</span></>
                                    ) : (
                                        <><Lock size={14} /><span>Need {action.cost - balance} more</span></>
                                    )}
                                </button>
                            </div>
                        </div>
                    );
                })}
            </div>

            {!anonymousId && (
                <div className={`p-6 rounded-2xl border border-dashed text-center ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                    <Lock size={24} className="mx-auto mb-2 text-gray-500" />
                    <p className="text-xs font-bold text-gray-500 italic">Sign in to unlock spending.</p>
                </div>
            )}
        </div>
    );
}

// ─── Streak Tab (unchanged) ───────────────────────────────────────────────────

function StreakTab({ streak, darkMode, votedPollsList = [], onGoVote }) {
    const days = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];
    const currentDayIndex = new Date().getDay() === 0 ? 6 : new Date().getDay() - 1;
    const todayStr = new Date().toDateString();
    const hasVotedToday = votedPollsList.some(p => p.voted_at && new Date(p.voted_at).toDateString() === todayStr);

    return (
        <div className="space-y-10 animate-in fade-in slide-in-from-bottom-4 duration-500">
            <div className={`p-10 rounded-[40px] border relative overflow-hidden shadow-xl ${darkMode ? 'bg-black border-gray-800' : 'bg-white border-gray-100'}`}>
                {!hasVotedToday && (
                    <div className="absolute inset-0 bg-red-500/5 animate-pulse pointer-events-none" />
                )}
                <div className="relative z-10">
                    <div className="flex justify-between items-start mb-10">
                        <div>
                            <div className="flex items-center space-x-2 mb-2">
                                <div className={`w-3 h-3 rounded-full ${hasVotedToday ? 'bg-green-500' : 'bg-red-500 animate-ping'}`} />
                                <span className={`text-xs font-black uppercase tracking-widest ${hasVotedToday ? 'text-green-500' : 'text-red-500'}`}>
                                    {hasVotedToday ? 'Today is safe' : 'Vote now to save your streak'}
                                </span>
                            </div>
                            <h3 className="text-5xl font-black tracking-tighter text-gray-900 dark:text-white">Daily Progress</h3>
                        </div>
                        <div className="text-right">
                            <div className="text-6xl font-black text-secondary-600 tabular-nums tracking-tighter">{streak}</div>
                            <div className="text-xs font-black text-gray-500 uppercase tracking-widest">Total Days</div>
                        </div>
                    </div>

                    <div className="flex justify-between items-end gap-3 mb-10">
                        {days.map((day, idx) => {
                            const isPast = idx < currentDayIndex;
                            const isCurrent = idx === currentDayIndex;
                            return (
                                <div key={idx} className="flex-1 flex flex-col items-center space-y-4">
                                    <div className={`w-full aspect-square rounded-2xl border-2 transition-all duration-700 flex items-center justify-center ${isPast ? 'bg-pink-500/10 border-pink-500/30' : isCurrent
                                        ? hasVotedToday ? 'bg-pink-500/20 border-pink-500/40 shadow-lg' : 'bg-gray-100 dark:bg-gray-800 border-red-500 border-dashed animate-pulse'
                                        : 'bg-gray-50 dark:bg-gray-800/10 border-gray-100 dark:border-gray-800'}`}>
                                        {isPast ? <Flame size={20} className="text-secondary-600" /> : isCurrent && !hasVotedToday ? <Zap size={20} className="text-red-500" /> : null}
                                    </div>
                                    <span className={`text-xs font-black ${isCurrent ? 'text-gray-900 dark:text-white' : 'text-gray-500'}`}>{day}</span>
                                </div>
                            );
                        })}
                    </div>

                    {!hasVotedToday && (
                        <div className="p-8 rounded-3xl bg-red-600 text-white flex flex-col md:flex-row items-center justify-between gap-6 shadow-2xl shadow-red-600/30">
                            <div className="flex items-center space-x-6">
                                <AlertCircle size={40} className="animate-pulse flex-shrink-0" />
                                <div>
                                    <h4 className="text-xl font-black tracking-tight mb-1">Missed Day Alert!</h4>
                                    <p className="text-sm font-bold opacity-90 italic">Vote now or lose your streak bonus.</p>
                                </div>
                            </div>
                            <button onClick={onGoVote} className="w-full md:w-auto px-10 py-4 bg-white text-red-600 rounded-2xl font-black uppercase text-sm tracking-widest hover:bg-gray-100 transition-all active:scale-95">Save My Streak</button>
                        </div>
                    )}
                </div>
            </div>

            <section>
                <div className="flex items-center space-x-3 mb-6">
                    <h4 className="text-lg font-black tracking-tight text-gray-900 dark:text-white">Upcoming Rewards</h4>
                    <div className="h-[2px] flex-1 bg-gray-100 dark:bg-gray-800 rounded-full" />
                </div>
                <div className="grid grid-cols-1 gap-4">
                    {[
                        { days: 7, title: 'Streak Bonus Unlocks', desc: 'Earn extra coins per vote.', icon: '⚡' },
                        { days: 30, title: 'Pro Data Access', desc: 'See deeper details on all polls.', icon: '💎' },
                        { days: 365, title: 'Honorary Ghost', desc: 'Exclusive badge and early access.', icon: '🏆' },
                    ].map(m => {
                        const isUnlocked = streak >= m.days;
                        const progress = Math.min(100, (streak / m.days) * 100);
                        return (
                            <div key={m.days} className={`p-6 rounded-3xl border transition-all ${isUnlocked ? (darkMode ? 'bg-green-900/10 border-green-500/30' : 'bg-green-50 border-green-500/20') : (darkMode ? 'bg-gray-800/40 border-gray-700' : 'bg-white border-gray-100 shadow-sm')}`}>
                                <div className="flex items-center justify-between mb-4">
                                    <div className="flex items-center space-x-4">
                                        <div className={`w-12 h-12 rounded-2xl flex items-center justify-center text-2xl ${isUnlocked ? 'bg-green-100 dark:bg-green-900/30' : 'bg-gray-100 dark:bg-gray-800'}`}>{m.icon}</div>
                                        <div>
                                            <h5 className="font-black text-base tracking-tight text-gray-900 dark:text-white">{m.days}-Day Goal</h5>
                                            <p className="text-xs font-bold text-gray-500 italic">{m.title}: {m.desc}</p>
                                        </div>
                                    </div>
                                    {isUnlocked ? (
                                        <CheckCircle2 className="text-green-500 flex-shrink-0" size={22} />
                                    ) : (
                                        <div className="text-2xs font-black uppercase text-gray-500 flex-shrink-0">{m.days - streak}d left</div>
                                    )}
                                </div>
                                {!isUnlocked && progress > 0 && (
                                    <div className="h-1.5 w-full bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden">
                                        <div className="h-full bg-blue-500 rounded-full transition-all duration-1000" style={{ width: `${progress}%` }} />
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </section>
        </div>
    );
}
